import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import AbstractLink from '@databases/content-link/AbstractLink';
import BrokenLink from '@databases/content-link/BrokenLink';
import EntryLink from '@databases/content-link/EntryLink';
import { EntryIconColor } from '@databases/icons';
import { messages } from '@databases/intl/messages';
import { logError, logWarning } from '@databases/logs';
import { useCallbackEffect } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { BacklinkDisplayType, type DatabaseFieldType } from '@databases/types/database';
import { getErrStatus } from '@databases/utils/confluence/fetch';
import { getBacklinkEntries } from '@databases/utils/database/field-types/backlink';
import { getDatabaseEntryRequired, getEntryName } from '@databases/utils/database/utils';
import { loadDatabaseByContentId } from '@databases/utils/redux/databases/actions';
import React, { type FunctionComponent, useCallback, useState } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { getSelectDatabaseDraft } from '../utils';

import { type ViewComponentProps } from './types';

const Container = styled.ul`
  list-style-type: none;
  padding: 0;

  > li + li {
    margin-top: ${token('space.075', '6px')};
  }
`;

const preventDefault = (e: React.MouseEvent | React.FocusEvent) => {
  // Prevent propagation to inline edit
  e.stopPropagation();
};

const BacklinkValueView: FunctionComponent<
  ViewComponentProps<DatabaseFieldType.BACKLINK>
> = ({ contentId, field, entryId, noFocus, isReadonly }) => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = defaultStoreUtils.useDispatch();
  const intl = useIntl();
  const { formatMessage } = intl;
  const [errMessage, setErrMessage] = useState('');

  useCallbackEffect(
    useCallback(async () => {
      setIsLoading(true);
      try {
        if (field.settings.contentId) {
          await dispatch(loadDatabaseByContentId(field.settings.contentId));
          setErrMessage('');
        }
      } catch (err: unknown) {
        const error = new Error(
          `Could not load database with content id ${field.settings.contentId}: ${err}`
        );
        const status = getErrStatus(err);
        let message = messages['broken-link.broken-link-text'];
        if (status && status === 403) {
          message = messages['restricted-link.restricted-link-text'];
          logWarning(error);
        } else if (status && status === 404) {
          logWarning(error);
        } else {
          logError(error);
        }
        setErrMessage(formatMessage(message));
      } finally {
        setIsLoading(false);
      }
    }, [dispatch, field.settings.contentId, formatMessage])
  );

  const [targetEntryRefs] = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      includeDeleted: true,
    });
    const entry = entryId && getDatabaseEntryRequired(databaseDraft, entryId);
    const targetEntries = entry
      ? getBacklinkEntries({ database: databaseDraft, field, entry }, state, {
          selectDatabase: getSelectDatabaseDraft(true),
        })
      : [];

    return [
      targetEntries.map((targetEntry) => {
        const entryName = getEntryName(targetEntry, state);

        return {
          databaseSpaceKey: targetEntry.database.space.key,
          databaseId: targetEntry.database.id,
          contentId: targetEntry.database.contentId,
          entryId: targetEntry.entry.id,
          title: entryName
            ? entryName
            : formatMessage(messages['unnamed-entities.unnamed-entry']),
        };
      }),
    ] as const;
  });

  if (isLoading) {
    return (
      <Container>
        <li>
          <AbstractLink noFocus iconBefore={<EntryIconColor label="" />}>
            <Box
              xcss={xcss({
                width: field.width ? Math.min(field.width - 60, 100) + 'px' : '100px',
                height: token('space.100', '8px'),
                margin: 'space.075',
                backgroundColor: 'color.skeleton',
                borderRadius: token('space.200', '16px'),
              })}
            />
          </AbstractLink>
        </li>
      </Container>
    );
  } else if (errMessage) {
    return <BrokenLink message={errMessage} />;
  } else {
    switch (field.settings.backlinkDisplayType) {
      case BacklinkDisplayType.NUMBER:
        return <>{targetEntryRefs.length}</>;

      default: {
        return (
          <Container>
            {targetEntryRefs.map((entryRef) => (
              <li key={entryRef.entryId}>
                <EntryLink
                  entryRef={entryRef}
                  noFocus={noFocus}
                  onClick={preventDefault}
                  onFocus={preventDefault}
                  contentId={contentId}
                  isReadonly={isReadonly}
                />
              </li>
            ))}
          </Container>
        );
      }
    }
  }
};

export default BacklinkValueView;
